<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import type { Snackbar, SnackbarVariant } from '@/types/widget'

export type { Snackbar, SnackbarVariant }

const props = withDefaults(defineProps<Snackbar>(), {
  variant: 'default',
  actionText: 'OK',
  duration: 3000,
  isInfinite: false,
  id: undefined,
  action: undefined
})

const emits = defineEmits(['close', 'action'])

const timeout = ref<number | null>(null)

const isTooltip = computed(() => props.variant === 'tooltip')

const containerClasses = computed(() => {
  if (isTooltip.value) {
    return 'flex items-center justify-center rounded-full bg-gray-900/95 backdrop-blur-md border border-violet-500/30 shadow-lg shadow-black/50 px-4 py-2 space-x-2 text-violet-100'
  }
  return 'flex justify-between items-center rounded-lg bg-gray-800 shadow-md shadow-indigo-800/50 border border-indigo-700 p-4 space-x-2'
})

const textClasses = computed(() => {
  if (isTooltip.value) {
    return 'text-xs sm:text-sm font-medium tracking-wide'
  }
  return 'text-sm lg:text-base'
})

const performAction = () => {
  emits('action')
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
  emits('close')
}

onMounted(() => {
  if (!props.isInfinite) {
    timeout.value = window.setTimeout(() => {
      emits('close')
    }, props.duration)
  }
})
</script>

<template>
  <div :class="containerClasses">
    <span :class="textClasses">{{ text }}</span>
    <span
      v-if="actionText && !isTooltip"
      data-testid="snackbar-action"
      class="uppercase tracking-wide font-bold cursor-pointer text-violet-600"
      @click="performAction"
    >
      {{ actionText }}
    </span>
  </div>
</template>
